// A normal inline search box in the topbar — type, see matching results drop
// down below, click or press Enter to go. No keyboard shortcut, no modal
// overlay; it behaves like an ordinary search field. Searches across pages
// (nav links) and, where the user has access, devices — not just navigation.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LayoutGrid, Monitor as MonitorIcon, Settings as SettingsIcon, LayoutDashboard, Clock } from "lucide-react";
import { apiGet } from "../../utils/api";
import { displayName } from "../../utils/displayName";
import { getRole } from "../../utils/authService";
import "./commandPalette.css";

// Settings and Monitoring are each one page with internal tabs (not separate
// routes), already deep-linkable via ?tab= (see ProductivityConfig's/
// MonitoringHub's useState/useSearchParams initializers) — listing each tab
// here lets search land directly on it instead of just the page. Static
// because these tabs are fixed in the source, not fetched.
const SETTINGS_ITEMS = [
  { href: "/settings?tab=teams", label: "Settings: Teams" },
  { href: "/settings?tab=apps", label: "Settings: Apps" },
  { href: "/settings?tab=alerts", label: "Settings: Alerts" },
  { href: "/settings?tab=attendance", label: "Settings: Attendance" },
  { href: "/settings?tab=screenshots", label: "Settings: Screenshots" },
  { href: "/settings?tab=sso", label: "Settings: Authentication" },
  // { href: "/settings?tab=integrations", label: "Settings: Integrations" }, // commented out — tab is disabled in ProductivityConfig, keep for later
  // Accounts/Salaries aren't ?tab= panels on /settings itself — they're their
  // own routes linked from a quick-link card inside it (see ProductivityConfig's
  // NAV_LINKS) — same access gate as the rest of Settings (reaching /settings
  // at all already requires productivity_config, which is all either needs).
  { href: "/role-accounts", label: "Settings: Accounts" },
  { href: "/cfo-salary-config", label: "Settings: Salaries" },
].map((it) => ({ ...it, group: "Settings", icon: SettingsIcon }));

// All four Monitoring tabs share one FeatureGate (see MonitoringHub.jsx's own
// comment) — no per-tab permission split needed, unlike Dashboard below.
const MONITORING_ITEMS = [
  { href: "/monitoring?tab=url", label: "Monitoring: URL" },
  { href: "/monitoring?tab=uploads", label: "Monitoring: File Uploads" },
  { href: "/monitoring?tab=changes", label: "Monitoring: File Changes" },
  { href: "/monitoring?tab=usb", label: "Monitoring: USB" },
].map((it) => ({ ...it, group: "Monitoring", icon: MonitorIcon }));

// Dashboard's tabs are gated per-tab (see DashboardHub.jsx's own canSee
// logic), not as one block like Settings/Monitoring, so this mirrors that
// gating instead of just checking whether /dashboard is in the sidebar —
// otherwise every role would see every tab in search, including ones that
// 404/redirect for them. The client role's tab set additionally depends on
// an async billing fetch DashboardHub itself does; duplicating that here
// would mean a second network call just for search, so client tenant owners
// see the tabs available by default (Overview, Finance, Risk & Compliance)
// rather than the exact fetched set.
function getDashboardItems(allowedViewSet) {
  const role = getRole();
  const items = [];
  const add = (href, label) => items.push({ href, label, group: "Dashboard", icon: LayoutDashboard });

  const canSeeExecutive = role === "admin" || role === "client" || allowedViewSet.has("executive_dashboard");
  const canSeeCfo = role === "admin" || role === "client" || allowedViewSet.has("cfo_dashboard");

  if (canSeeExecutive) {
    add("/dashboard?tab=overview", "Dashboard: Overview");
    add("/dashboard?tab=risk", "Dashboard: Risk & Compliance");
  }
  if (canSeeCfo) add("/dashboard?tab=cfo", "Dashboard: Finance");
  if (allowedViewSet.has("manager_dashboard")) add("/dashboard?tab=team", "Dashboard: Team");
  if (allowedViewSet.has("employee_dashboard")) add("/dashboard?tab=mywork", "Dashboard: My Work");

  return items;
}

function getAllowedViewSet() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("allowedViews") || "[]");
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
}

// Reads whatever nav links are currently rendered in the sidebar — that list
// is already computed by Sidebar from the real role/feature-gating logic, so
// this search can never show a route the current user doesn't actually have
// access to, and never needs to duplicate that permission logic itself.
function collectNavLinks() {
  const anchors = Array.from(document.querySelectorAll(".sidebar a.nav-link"));
  const seen = new Set();
  const pages = anchors.reduce((acc, el) => {
    const href = el.getAttribute("href") || "";
    const label =
      el.querySelector(".nav-label")?.textContent?.trim() ||
      el.getAttribute("title") ||
      "";
    if (!href || !label || seen.has(href)) return acc;
    seen.add(href);
    acc.push({ href, label, el, group: "Pages", icon: LayoutGrid });
    return acc;
  }, []);

  // Same permission signal as loadDevices below: only offer a page's inner
  // tabs as search results if the sidebar itself is already offering that
  // page's own top-level link.
  const hasSettingsAccess = document.querySelector('.sidebar a.nav-link[href="/settings"]');
  const hasMonitoringAccess = document.querySelector('.sidebar a.nav-link[href="/monitoring"]');
  const hasDashboardAccess = document.querySelector('.sidebar a.nav-link[href="/dashboard"]');

  return [
    ...pages,
    ...(hasSettingsAccess ? SETTINGS_ITEMS : []),
    ...(hasMonitoringAccess ? MONITORING_ITEMS : []),
    ...(hasDashboardAccess ? getDashboardItems(getAllowedViewSet()) : []),
  ];
}

export default function CommandPalette() {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [items, setItems] = useState([]);
  const [devices, setDevices] = useState([]);
  // Which result Up/Down has moved to; -1 means none yet, so Enter still
  // falls back to the top result until the user actually presses an arrow.
  const [activeIndex, setActiveIndex] = useState(-1);
  const devicesLoaded = useRef(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const q = query.trim().toLowerCase();
  const allItems = [...items, ...devices];
  // Match against searchText (name + hostname + raw agentId, all lower-cased)
  // rather than just the pretty label — a device with a friendly name set
  // still had its raw agentId searchable this way, which the label alone
  // never showed.
  const results = q ? allItems.filter((it) => (it.searchText || it.label.toLowerCase()).includes(q)) : [];
  const showDropdown = focused && q.length > 0;

  // A fresh query invalidates whatever index the previous result list had
  // highlighted — without this, Down after retyping could land on the wrong
  // (or a now out-of-range) item.
  useEffect(() => {
    setActiveIndex(-1);
  }, [q]);

  // Keep the highlighted row in view as arrow keys move past the visible
  // edge of the scrollable dropdown.
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = wrapRef.current?.querySelector(`.cmdk-item[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Devices are only ever fetched when the current role can actually reach
  // the Devices page — reusing the same "read the sidebar" permission signal
  // as nav links, instead of re-deriving role rules here. The permission
  // check itself is cheap (a DOM query) and re-runs on every focus rather
  // than being cached, since the sidebar may not have rendered its own nav
  // links yet on the very first focus right after login — caching a false
  // reading there would permanently hide devices for the rest of the
  // session. Only a *successful* fetch is cached, so a transient network
  // failure on attempt one doesn't lock out every later search too.
  const loadDevices = () => {
    if (devicesLoaded.current) return;
    const hasDevicesAccess = document.querySelector('.sidebar a.nav-link[href="/devices"]');
    if (!hasDevicesAccess) return;
    // Attendance is a separate permission from Devices (own sidebar link),
    // so a person search only offers it when the current role can actually
    // reach /all-attendance — same "read the sidebar" gating as everywhere
    // else in this file, not a re-derived permission rule.
    const hasAttendanceAccess = document.querySelector('.sidebar a.nav-link[href="/all-attendance"]');
    apiGet("/api/agents")
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        devicesLoaded.current = true;
        // Built as two separate passes (all Devices, then all Attendance)
        // rather than interleaved per-agent, so a broad query that matches
        // several people still renders as two clean sections instead of
        // alternating "Devices"/"Attendance" headers per match — `results`
        // downstream is a plain filter() that preserves this order.
        const deviceResults = [];
        const attendanceResults = [];
        data.forEach((a) => {
          const name = displayName(a);
          const searchText = [name, a.hostname, a.agentId].filter(Boolean).join(" ").toLowerCase();
          // Lands on the Devices grid pre-filtered to this one device
          // (View Screen/Task Manager/Software/Performance from there),
          // rather than jumping straight into its bare hardware-info page
          // with no other options visible.
          deviceResults.push({
            href: `/devices?search=${encodeURIComponent(a.agentId)}`,
            label: `${name}${a.hostname ? ` (${a.hostname})` : ""}`,
            searchText,
            group: "Devices",
            icon: MonitorIcon,
          });
          if (hasAttendanceAccess) {
            attendanceResults.push({
              href: `/analytics/employee/${encodeURIComponent(a.agentId)}`,
              label: `${name} — Attendance`,
              searchText,
              group: "Attendance",
              icon: Clock,
            });
          }
        });
        setDevices([...deviceResults, ...attendanceResults]);
      })
      .catch((err) => console.error("Failed to load devices for search:", err));
  };

  const goTo = (item) => {
    setQuery("");
    setFocused(false);
    // Result buttons preventDefault their mousedown so the input keeps real
    // DOM focus through the click (otherwise the click never registers) —
    // blur it explicitly now that the interaction is actually done, so a
    // later click properly re-triggers onFocus instead of the input already
    // "being focused" from the browser's point of view.
    inputRef.current?.blur();
    // Clicking the real sidebar anchor reuses its exact existing navigation
    // handler (route change, mobile-sidebar close, etc.) instead of us
    // reimplementing it here — but only if it's still actually in the page.
    // `items` is only refreshed on focus, so if the sidebar re-rendered in
    // between (a collapse/expand toggle, a permission refresh, ...) the
    // captured element can go stale; .click() on a detached node silently
    // does nothing, which is exactly the "sometimes doesn't work" symptom.
    if (item.el && document.body.contains(item.el)) {
      item.el.click();
    } else {
      navigate(item.href);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setQuery(""); e.target.blur(); }
    else if (e.key === "ArrowDown") {
      if (!results.length) return;
      e.preventDefault(); // don't also move the input caret
      setActiveIndex((i) => (i + 1 >= results.length ? 0 : i + 1));
    } else if (e.key === "ArrowUp") {
      if (!results.length) return;
      e.preventDefault();
      setActiveIndex((i) => (i - 1 < 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      const target = results[activeIndex] || results[0];
      if (target) goTo(target);
    }
  };

  // Close the dropdown on an outside click (blur alone would fire before a
  // result's onClick registers, so this handles the click-away case instead).
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  return (
    <div className="topnav-search-wrap" ref={wrapRef}>
      <div className="topnav-search-pill">
        <Search size={14} />
        <input
          ref={inputRef}
          className="topnav-search-input"
          placeholder="Search…"
          value={query}
          onFocus={() => { setItems(collectNavLinks()); loadDevices(); setFocused(true); }}
          // Result buttons use onMouseDown preventDefault so the input never
          // actually loses DOM focus when clicking a result (that's what
          // makes the click register at all) — but goTo() still sets
          // `focused` to false afterward for the (already-closed) dropdown.
          // Since the input's real focus state and this flag can now
          // disagree, re-derive it from the one thing that's actually true:
          // if you're typing in the box, you're focused on it. Without this,
          // typing a second search after clicking a result updated `query`
          // but the dropdown never came back, since `focused` was stuck false.
          onChange={(e) => { setQuery(e.target.value); setFocused(true); }}
          onKeyDown={handleKeyDown}
        />
      </div>
      {showDropdown && (
        <div className="cmdk-dropdown" role="listbox">
          {results.length === 0 && (
            <div className="cmdk-empty">No matches for "{query}"</div>
          )}
          {results.map((it, idx) => {
            const Icon = it.icon;
            const isNewGroup = idx === 0 || results[idx - 1].group !== it.group;
            const isActive = idx === activeIndex;
            return (
              <React.Fragment key={it.href}>
                {isNewGroup && <div className="cmdk-group-label">{it.group}</div>}
                <button
                  className={`cmdk-item${isActive ? " cmdk-item--active" : ""}`}
                  data-idx={idx}
                  onMouseDown={(e) => e.preventDefault()} // keep focus so onClick still fires
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => goTo(it)}
                  role="option"
                  aria-selected={isActive}
                >
                  {Icon && <Icon size={14} className="cmdk-item-icon" />}
                  <span>{it.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
